import { motion } from 'framer-motion'
import { useLoyaltyProgress, useLoyaltyPoints, useLoyaltyLevel } from '../../stores/loyaltyStore'
import { ACHIEVEMENT_LEVELS, calculatePointsToNextLevel } from '../../types/loyalty'

interface ProgressBarProps {
  showLabel?: boolean
  showPoints?: boolean
  size?: 'sm' | 'md' | 'lg'
  variant?: 'gradient' | 'solid' | 'premium'
  className?: string
  animated?: boolean
}

const ProgressBar = ({
  showLabel = true,
  showPoints = true,
  size = 'md',
  variant = 'premium',
  className = '',
  animated = true
}: ProgressBarProps) => {
  const { pointsToNext, percentage } = useLoyaltyProgress()
  const totalPoints = useLoyaltyPoints()
  const currentLevel = useLoyaltyLevel()

  // Get next achievement - always show the first achievement we need to unlock
  const levels = Object.keys(ACHIEVEMENT_LEVELS) as Array<keyof typeof ACHIEVEMENT_LEVELS>

  // Find the first achievement that requires more points than we currently have
  const nextAchievement = levels
    .map(level => ACHIEVEMENT_LEVELS[level])
    .find(achievement => totalPoints < achievement.pointsRequired) || null

  const currentAchievement = ACHIEVEMENT_LEVELS[currentLevel]

  // If user has max level
  if (!nextAchievement) {
    return (
      <div className={`${className}`}>
        {showLabel && (
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900">
              ¡Nivel Máximo Alcanzado!
            </span>
            <span className="text-sm font-bold text-purple-600">
              {currentAchievement.name}
            </span>
          </div>
        )}
        <div className="w-full bg-gradient-to-r from-purple-400 to-pink-400 rounded-full h-3 shadow-inner">
          <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
            <span className="text-xs font-bold text-white">MÁXIMO</span>
          </div>
        </div>
      </div>
    )
  }

  // Size configurations
  const sizeConfig = {
    sm: {
      height: 'h-2',
      text: 'text-xs',
      spacing: 'mb-1'
    },
    md: {
      height: 'h-3',
      text: 'text-sm',
      spacing: 'mb-2'
    },
    lg: {
      height: 'h-4',
      text: 'text-base',
      spacing: 'mb-3'
    }
  }

  const config = sizeConfig[size]

  // Get progress bar styles based on variant and next achievement
  const getProgressBarStyles = () => {
    const nextColor = nextAchievement.frameColor

    switch (variant) {
      case 'gradient':
        return {
          background: `linear-gradient(90deg, ${currentAchievement.frameColor}20, ${nextColor}20)`,
          progressBg: `linear-gradient(90deg, ${currentAchievement.frameColor}, ${nextColor})`
        }
      case 'solid':
        return {
          background: '#f3f4f6',
          progressBg: nextColor
        }
      case 'premium':
      default:
        return {
          background: `linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.3))`,
          progressBg: `linear-gradient(90deg, ${currentAchievement.frameColor}, ${nextColor})`,
          shadow: `0 4px 20px ${nextColor}30`
        }
    }
  }

  const styles = getProgressBarStyles()

  return (
    <div className={`w-full ${className}`} role="progressbar" aria-valuenow={percentage} aria-valuemin={0} aria-valuemax={100}>
      {/* Labels */}
      {(showLabel || showPoints) && (
        <div className={`flex items-center justify-between ${config.spacing}`}>
          {showLabel && (
            <div className="flex items-center gap-2">
              <span className={`font-medium text-gray-900 ${config.text}`}>
                Progreso hacia {nextAchievement.name}
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold text-white shadow-sm`}
                style={{ backgroundColor: nextAchievement.frameColor }}
              >
                {Math.round(percentage)}%
              </span>
            </div>
          )}

          {showPoints && (
            <span className={`font-bold text-gray-700 ${config.text}`}>
              {pointsToNext.toLocaleString()} puntos restantes
            </span>
          )}
        </div>
      )}

      {/* Progress Bar Container */}
      <div
        className={`w-full rounded-full ${config.height} relative overflow-hidden shadow-inner backdrop-blur-sm border border-white/20`}
        style={{
          background: styles.background,
          boxShadow: styles.shadow
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="h-full w-full bg-gradient-to-r from-transparent via-white to-transparent animate-pulse" />
        </div>

        {/* Progress Fill */}
        <motion.div
          className={`${config.height} rounded-full relative overflow-hidden`}
          style={{
            background: styles.progressBg,
            width: animated ? '0%' : `${percentage}%`
          }}
          initial={animated ? { width: '0%' } : false}
          animate={animated ? { width: `${percentage}%` } : false}
          transition={{
            duration: 1.5,
            ease: "easeOut",
            delay: 0.2
          }}
        >
          {/* Shimmer Effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{
              x: ['-100%', '100%']
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
              repeatDelay: 1
            }}
          />

          {/* Inner Glow */}
          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/10 to-white/20" />
        </motion.div>

        {/* Achievement Milestone Markers */}
        <div className="absolute inset-y-0 left-0 right-0 flex items-center">
          {/* Current Level Marker */}
          <div
            className="absolute w-1 bg-white rounded-full shadow-lg z-10"
            style={{
              height: '120%',
              left: '0%',
              top: '-10%',
              backgroundColor: currentAchievement.frameColor
            }}
          />

          {/* Next Level Marker */}
          <div
            className="absolute w-1 bg-white rounded-full shadow-lg z-10"
            style={{
              height: '120%',
              right: '0%',
              top: '-10%',
              backgroundColor: nextAchievement.frameColor
            }}
          />
        </div>
      </div>

      {/* Achievement Preview */}
      {showLabel && (
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full border-2 border-white shadow-sm"
              style={{ backgroundColor: currentAchievement.frameColor }}
              aria-hidden="true"
            />
            <span className="text-xs text-gray-600">
              {currentAchievement.name}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">
              {nextAchievement.name}
            </span>
            <div
              className="w-3 h-3 rounded-full border-2 border-white shadow-sm"
              style={{ backgroundColor: nextAchievement.frameColor }}
              aria-hidden="true"
            />
          </div>
        </div>
      )}

      {/* Screen Reader Information */}
      <div className="sr-only">
        Progreso de lealtad: {Math.round(percentage)}% completado hacia el logro {nextAchievement.name}.
        Necesitas {pointsToNext} puntos más de un total de {nextAchievement.pointsRequired} puntos requeridos.
        Actualmente tienes {totalPoints} puntos y el nivel {currentAchievement.name}.
      </div>
    </div>
  )
}

export default ProgressBar